import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import PlainDropdown from 'app/common/ui-next/plain-dropdown'
import { RecipientUID, SenderUID, isParent } from 'app/dataTypes/transfer/uid'
import useCallbackRef from 'app/common/hooks/useCallbackRef'

import toFixedValue from 'app/common/lib/toFixedValue'
import { DropdownItem } from './DropdownItem'
import { DropdownPlaceholder } from './DropdownPlaceholder'

import styles from './TransferDropdown.module.scss'

type Props = {
  type: 'from' | 'to'
  disabled?: boolean
  items: Array<{
    UID: SenderUID | RecipientUID
    name: string
    balance: number
    gender: 'male' | 'female' | 'androgynous'
    photo: string | null
  }> | null
  selectedUID: SenderUID | RecipientUID | null
  onChange: (UID: SenderUID | RecipientUID) => void
}

const getNameAsOwn = (type: 'from' | 'to', def: string) => {
  if (type === 'from') return 'От меня'
  if (type === 'to') return 'Мне'
  return def
}

const TransferDropdown = ({ type, disabled, items, selectedUID, onChange }: Props) => {
  const { t } = useTranslation('finances')

  const label =
    type === 'from' ? t('transfer_from_label', 'От кого') : t('transfer_to_label', 'Кому')

  const placeholderTitle =
    type === 'from'
      ? t('transfer_select_sender', 'Выберите отправителя')
      : t('transfer_select_receiver', 'Выберите получателя')

  const onChangeRef = useCallbackRef(onChange)

  const handleChange = useCallback(
    ({ id }: { id: SenderUID | RecipientUID }) => {
      onChangeRef.current?.(id)
    },
    [onChangeRef],
  )

  const dropdownItems =
    items?.map(({ UID, name, balance, gender, photo }) => {
      return (
        <DropdownItem
          key={UID}
          id={UID}
          // !!! сейчас только родитель может пользоваться переводами, поэтому isParent
          title={isParent(UID) ? getNameAsOwn(type, name) : name}
          balance={toFixedValue(balance)}
          photo={photo}
          gender={gender}
          selected={selectedUID === UID}
          disabled={disabled}
        />
      )
    }) || null

  return (
    <div>
      <PlainDropdown
        id={type}
        label={label}
        onChange={handleChange}
        arrowClassName={styles.ddArrow}
        buttonClassName={styles.ddMain}
        itemClassName={styles.ddItem}
        labelClassName={styles.ddLabel}
        disabled={disabled}
        placeholder={
          <DropdownPlaceholder title={placeholderTitle} direction={type} disabled={disabled} />
        }
      >
        {dropdownItems}
      </PlainDropdown>
    </div>
  )
}

export default TransferDropdown
